import { useEffect, useState, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { ThemeProvider, useTheme } from "@/theme/ThemeProvider";

// Pages
import { Landing } from "@/pages/Landing";
import { Dashboard } from "@/pages/Dashboard";
import { GigManager } from "@/pages/GigManager";
import ComponentHarness from "@/pages/dev/ComponentHarness";
import { CalendarView } from "@/pages/CalendarView";
import { GigEnrichment } from "@/pages/GigEnrichment";
import { SocialGenerator } from "@/pages/SocialGenerator";
import { BatchSocialGenerator } from "@/pages/BatchSocialGenerator";
import { Scene } from "@/pages/Scene";
import { InvoiceList } from "@/pages/InvoiceList";
import { InvoiceForm } from "@/pages/InvoiceForm";
import { InvoiceSettings } from "@/pages/InvoiceSettings";
import { SetlistLibrary } from "@/pages/SetlistLibrary";
import { TravelSettings } from "@/pages/TravelSettings";
import { MagicLinkVerify } from "@/pages/MagicLinkVerify";
import { ResetPassword } from "@/pages/ResetPassword";
import { Settings } from "@/pages/Settings";
import { CareerStats } from "@/pages/CareerStats";
import SharedGigView from "@/pages/SharedGigView";
import RSVPPage from "@/pages/RSVPPage";
import GigInviteView from "@/pages/GigInviteView";
import GigPreviewPage from "@/pages/GigPreviewPage";
import MusicianProfile from "@/pages/MusicianProfile";
import EmbedGigs from "@/pages/EmbedGigs";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import WelcomePage from "@/pages/WelcomePage";
import PrivacyPage from "@/pages/PrivacyPage";
import OnboardingCarousel from "@/pages/OnboardingCarousel";
import { attachStoredReferral } from "@/lib/referral";

import { InstallPrompt } from "@/components/InstallPrompt";
import { OfflineBanner } from "@/components/OfflineBanner";
import { attachOfflineInterceptors } from "@/lib/offlineInterceptors";
import { attachMutationQueueInterceptor } from "@/lib/mutationQueue";
import { attachSyncEngine } from "@/lib/syncEngine";
import { scheduleWarm } from "@/lib/cacheWarmer";
import { getProfile as getCachedProfile, clearProfile as clearCachedProfile } from "@/lib/offlineCache";

// API base — always use relative path so requests go to same origin (works on preview + production)
export const API = process.env.REACT_APP_API_URL || '/api';

// Primary auth path is the HttpOnly session_token cookie set by the backend
// (sent automatically with credentials: 'include' / withCredentials: true).
// The frontend (www.fretless.life) and backend (Railway) are different
// domains, so that cookie is third-party from the browser's perspective —
// this used to be reliably dropped only by iOS Safari's ITP, but Chrome has
// since rolled out the same third-party-cookie blocking, so a login can
// succeed (session created, cookie set) while every subsequent request 401s
// because the browser never sends the cookie back. localStorage is JS-
// readable (an XSS exposure cookies don't have), but a token that's silently
// never sent isn't a security tradeoff — it's broken auth — so this fallback
// now runs on every browser rather than iOS Safari only.
export const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
});

const SESSION_KEY = "aos_session_token";

export const storeSessionToken = (token) => {
  if (!token) return;
  try { localStorage.setItem(SESSION_KEY, token); } catch {}
};

export const clearSessionToken = () => {
  try { localStorage.removeItem(SESSION_KEY); } catch {}
};

// Request interceptor: attach the Bearer token alongside the cookie on every
// request. The backend accepts either — whichever the browser actually
// delivers wins.
apiClient.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem(SESSION_KEY);
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {}
  return config;
});

// Offline cache wiring (Phase 1 of offline mode):
//  - write-through GET responses to IndexedDB
//  - fall back to cached data when a GET fails at the network level
// Mutating requests are NOT touched here — those go through the mutation
// queue (Step 4).
attachOfflineInterceptors(apiClient);
// Write path: queue failed mutating requests and apply an optimistic
// update to the local cache. Step 5 drains the queue when we're back online.
attachMutationQueueInterceptor(apiClient);

// Sync engine: drains the offline mutation queue on `online` and `focus`.
// On 409 conflicts we apply last-write-wins and surface a toast so the user
// knows their offline edit overwrote a newer server version.
attachSyncEngine(apiClient, {
  onConflict: ({ rec, unresolved }) => {
    const label = rec?.url || "an offline change";
    if (unresolved) {
      toast.error(`Couldn't sync ${label} — server rejected the change. Saved to failed list.`);
    } else {
      toast.message(`Server had newer changes for ${label} — your offline edit was kept.`);
    }
  },
});

// Auth Context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Helper: Extract session_id from multiple sources (query params primary, hash fallback, sessionStorage)
const getSessionId = () => {
  // 1. Check query params FIRST (primary flow — works on iOS Safari which strips hash fragments)
  const search = window.location.search;
  if (search && search.includes('session_id=')) {
    const sid = new URLSearchParams(search).get('session_id');
    if (sid) return sid;
  }
  // 2. Check URL hash (legacy/fallback)
  const hash = window.location.hash;
  if (hash && hash.includes('session_id=')) {
    const sid = new URLSearchParams(hash.substring(1)).get('session_id');
    if (sid) return sid;
  }
  // 3. Check sessionStorage (captured by inline script in index.html before React loaded)
  try {
    const sid = sessionStorage.getItem('pending_session_id');
    if (sid) return sid;
  } catch {}
  return null;
};

const clearPendingSessionId = () => {
  try { sessionStorage.removeItem('pending_session_id'); } catch {}
  try { sessionStorage.removeItem('auth_pending'); } catch {}
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // FAST PATH: If inline script in index.html already exchanged the session_id
    if (window.__AUTH_RESULT && window.__AUTH_RESULT.success) {
      const data = window.__AUTH_RESULT;
      // Clear globals so AppRouter stops rendering AuthCallback
      delete window.__AUTH_RESULT;
      delete window.__AUTH_EXCHANGE;
      if (data.session_token) storeSessionToken(data.session_token);
      setUser(data.user);
      setLoading(false);
      // Save browser timezone
      saveTimezone();
      return;
    }

    // CRITICAL: If returning from OAuth callback (inline exchange still in flight or pending),
    // skip the /me check. AuthCallback will handle it.
    const pendingSessionId = getSessionId();
    if (pendingSessionId || window.__AUTH_EXCHANGE) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await apiClient.get("/auth/me");
      setUser(response.data);
      // Once we know we're authenticated AND online, warm the picker caches
      // in the background so add-gig pickers (bands/collaborators/venues)
      // work when the user next loses signal. No-ops if offline or recently
      // warmed.
      scheduleWarm(apiClient);
    } catch (error) {
      // Offline session fallback: if /auth/me failed at the network level
      // (no response — i.e. we're offline, not a 401), try to render the app
      // from the last cached profile so the user can read their data.
      // A real 401/403 still clears the session — those are deliberate
      // server signals that the user should be signed out.
      const isNetworkFail = !error?.response;
      if (isNetworkFail) {
        try {
          const cached = await getCachedProfile();
          if (cached) {
            setUser({ ...cached, _offlineSession: true });
            return;
          }
        } catch {}
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveTimezone = async () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      await apiClient.put("/user/timezone", { timezone: tz });
    } catch {}
  };

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // Mark that we're about to do auth — used for fallback detection on mobile
    try { sessionStorage.setItem('auth_pending', Date.now().toString()); } catch {}
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `${API}/auth/google`;
  };

  const logout = async () => {
    try {
      await apiClient.post("/auth/logout");
      clearSessionToken();
      // Drop the cached profile so a different user can't load it from IDB
      // on next cold boot (shared-device case).
      try { await clearCachedProfile(); } catch {}
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      clearSessionToken();
      try { await clearCachedProfile(); } catch {}
      console.error("Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

const processAuth = async () => {
      // Handle direct session_token from Google OAuth callback.
      // Token arrives in the URL **fragment** (not query string) so it doesn't
      // leak via Referer headers, server access logs, or third-party requests.
      // Fall back to query string for backwards-compat during deploy.
      const hash = window.location.hash || "";
      const hashParams = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
      const urlParams = new URLSearchParams(window.location.search);
      const directToken = hashParams.get('session_token') || urlParams.get('session_token');
      const isNew = (hashParams.get('is_new') || urlParams.get('is_new')) === 'true';
      const importedGigs = parseInt(hashParams.get('imported_gigs') || urlParams.get('imported_gigs') || '0', 10);

      if (directToken) {
        // Backend already set the HttpOnly cookie on the redirect; this call
        // also stores the token in localStorage IFF we're on iOS Safari
        // (no-op on every other browser).
        storeSessionToken(directToken);
        try {
          const meRes = await apiClient.get("/auth/me", {
            headers: { Authorization: `Bearer ${directToken}` }
          });
          setUser(meRes.data);
          // Strip the fragment + query string so the token isn't preserved in
          // browser history once we land on /dashboard.
          window.history.replaceState(null, '', '/dashboard');
          if (importedGigs > 0) {
            toast.success(`${isNew ? "Welcome to Fretless!" : "Welcome back!"} We've added ${importedGigs} confirmed ${importedGigs === 1 ? 'gig' : 'gigs'} to your account.`);
          } else {
            toast.success(isNew ? "Welcome to Fretless!" : "Welcome back!");
          }
          if (isNew) {
            navigate('/welcome', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        } catch (e) {
          toast.error("Authentication failed. Please try again.");
          navigate('/', { replace: true });
        }
        return;
      }
    // FAST PATH: Await the inline script's fetch (fired at page load, before React)
      if (window.__AUTH_EXCHANGE) {
        try {
          const data = await window.__AUTH_EXCHANGE;
          // Clear globals BEFORE navigating so AppRouter renders the dashboard, not AuthCallback
          delete window.__AUTH_EXCHANGE;
          delete window.__AUTH_RESULT;
          if (data && data.success) {
            if (data.session_token) storeSessionToken(data.session_token);
            setUser(data.user);
            clearPendingSessionId();
            window.history.replaceState(null, '', '/dashboard');
            toast.success(`Welcome, ${data.user.name}!`);
            // Reload full user profile (ensures subscription data is current)
            try {
              const meRes = await apiClient.get("/auth/me");
              setUser(meRes.data);
            } catch {}
            // Save browser timezone
            try {
              const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
              apiClient.put("/user/timezone", { timezone: tz });
            } catch {}
            if (data.is_new_signup) {
              await attachStoredReferral(apiClient);
              navigate('/welcome', { replace: true });
            } else {
              navigate('/dashboard', { replace: true });
            }
            return;
          }
        } catch (e) {
          // Clear globals on failure too
          delete window.__AUTH_EXCHANGE;
          delete window.__AUTH_RESULT;
          // Inline exchange failed; React fallback will retry below.
        }
      }

      // FALLBACK: Exchange session_id via React (if inline fetch failed)
      const sessionId = getSessionId();

      if (!sessionId) {
        // Check if we were in the middle of an auth flow (set before redirect)
        const authPending = sessionStorage.getItem('auth_pending');
        clearPendingSessionId();

        if (authPending) {
          // Auth was attempted but session_id was lost (iOS Safari hash stripping)
          toast.error("Sign-in didn't complete. Please try again.");
        } else {
          toast.error("Authentication failed");
        }
        navigate('/', { replace: true });
        return;
      }

      // Clear the pending session_id now that we're using it
      clearPendingSessionId();

      try {
        const response = await apiClient.post("/auth/session", {
          session_id: sessionId
        });

        if (response.data.success) {
          if (response.data.session_token) storeSessionToken(response.data.session_token);
          setUser(response.data.user);
          // Clean URL (remove hash/query artifacts)
          window.history.replaceState(null, '', '/dashboard');
          const importedCount = response.data.imported_gigs_count || 0;
          toast.success(
            importedCount > 0
              ? `Welcome, ${response.data.user.name}! We've added ${importedCount} confirmed ${importedCount === 1 ? 'gig' : 'gigs'} to your account.`
              : `Welcome, ${response.data.user.name}!`
          );
          // Reload full user profile (ensures subscription data is current)
          try {
            const meRes = await apiClient.get("/auth/me");
            setUser(meRes.data);
          } catch {}
          // Save browser timezone
          try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            apiClient.put("/user/timezone", { timezone: tz });
          } catch {}
          if (response.data.is_new_signup) {
            await attachStoredReferral(apiClient);
            navigate('/welcome', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        } else {
          throw new Error("Session creation failed");
        }
      } catch (error) {
        console.error("Auth callback error:", error);
        toast.error("Authentication failed. Please try again.");
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Redirect /gigs/:id → /gigs/:id/edit (handles old bookmarks and missed navigate calls)
const GigIdRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/gigs/${id}/edit`} replace />;
};

// App Router
const AppRouter = () => {
  const location = useLocation();
  
  // Dynamic page titles
  useEffect(() => {
    const titles = {
      '/': 'Fretless',
      '/dashboard': 'Dashboard — Fretless',
      '/gigs': 'Gigs — Fretless',
      '/gigs/new': 'New Gig — Fretless',
      '/calendar': 'Calendar — Fretless',
      '/social': 'Social Posts — Fretless',
      '/social/batch': 'Batch Posts — Fretless',
      '/scene': 'Scene — Fretless',
      '/collaborators': 'Scene — Fretless',
      '/money': 'Money — Fretless',
      '/invoices': 'Money — Fretless',
      '/invoices/new': 'New Invoice — Fretless',
      '/setlists': 'Setlists — Fretless',
      '/settings': 'Settings — Fretless',
      '/settings/travel': 'Settings — Fretless',
      '/stats': 'Career Stats — Fretless',
      '/welcome': 'Welcome — Fretless',
      '/privacy': 'Privacy Policy — Fretless',
    };
    const path = location.pathname;
    // If auth modal is showing on landing, use sign-in title
    document.title = titles[path] || (path.includes('/gigs/') ? 'Edit Gig — Fretless' : 'Fretless');
  }, [location.pathname]);

  // Check for session_id or pending inline exchange (query params primary, hash fallback, sessionStorage)
const pendingSessionId = getSessionId();
if (pendingSessionId || window.__AUTH_EXCHANGE || window.__AUTH_RESULT || location.search?.includes('session_id=') || location.search?.includes('session_token=') || location.hash?.includes('session_id=') || location.hash?.includes('session_token=')) {
  return <AuthCallback />;
}

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth/magic-link" element={<MagicLinkVerify />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/share/gig/:shareToken" element={<SharedGigView />} />
      <Route path="/dep/gig/:depToken" element={<SharedGigView mode="dep" />} />
      <Route path="/share/gig/:token/rsvp" element={<RSVPPage />} />
      <Route path="/gig-invite/:token" element={<GigInviteView />} />
      <Route path="/gig-preview/:token" element={<GigPreviewPage />} />
      <Route path="/musician/:username" element={<MusicianProfile />} />
      {/* Embeddable public-gigs widget — loaded inside an <iframe> on a
          musician's own website. Public, no auth, no app chrome. */}
      <Route path="/embed/gigs/:username" element={<EmbedGigs />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      {/* Public dev harness — render components with mock data, no auth.
          Verify design-sync changes here before commit (live app is gated). */}
      <Route path="/dev/components" element={<ComponentHarness />} />
      <Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      <Route path="/gigs" element={
        <ProtectedRoute><GigManager /></ProtectedRoute>
      } />
      <Route path="/gigs/new" element={
        <ProtectedRoute><GigManager mode="create" /></ProtectedRoute>
      } />
      <Route path="/gigs/:id/edit" element={
        <ProtectedRoute><GigManager mode="edit" /></ProtectedRoute>
      } />
      <Route path="/gigs/:id" element={<GigIdRedirect />} />
      <Route path="/calendar" element={
        <ProtectedRoute><CalendarView /></ProtectedRoute>
      } />
      <Route path="/enrich" element={
        <ProtectedRoute><GigEnrichment /></ProtectedRoute>
      } />
      <Route path="/social" element={
        <ProtectedRoute><SocialGenerator /></ProtectedRoute>
      } />
      <Route path="/social/batch" element={
        <ProtectedRoute><BatchSocialGenerator /></ProtectedRoute>
      } />
      <Route path="/collaborators" element={
        <ProtectedRoute><Scene /></ProtectedRoute>
      } />
      <Route path="/scene" element={
        <ProtectedRoute><Scene /></ProtectedRoute>
      } />
      {/* /money is the canonical Money hub URL. /invoices kept as an alias
          so old bookmarks and the gig-card "Create invoice" deep-link still
          work — both render the same component. */}
      <Route path="/money" element={
        <ProtectedRoute><InvoiceList /></ProtectedRoute>
      } />
      <Route path="/invoices" element={
        <ProtectedRoute><InvoiceList /></ProtectedRoute>
      } />
      <Route path="/invoices/new" element={
        <ProtectedRoute><InvoiceForm mode="create" /></ProtectedRoute>
      } />
      <Route path="/invoices/settings" element={
        <ProtectedRoute><InvoiceSettings /></ProtectedRoute>
      } />
      <Route path="/invoices/:id" element={
        <ProtectedRoute><InvoiceForm mode="edit" /></ProtectedRoute>
      } />
      <Route path="/setlists" element={
        <ProtectedRoute><SetlistLibrary /></ProtectedRoute>
      } />
      <Route path="/settings/travel" element={
        <ProtectedRoute><TravelSettings /></ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute><Settings /></ProtectedRoute>
      } />
      <Route path="/stats" element={
        <ProtectedRoute><CareerStats /></ProtectedRoute>
      } />
    </Routes>
  );
};

// Global Install Prompt — shows on any authenticated page when flag is false
const GlobalInstallPrompt = () => {
  const { user, setUser } = useAuth();

  if (!user || user.has_seen_install_prompt !== false) return null;

  const handleDismiss = () => {
    // Optimistically update user state so prompt hides immediately
    setUser(prev => prev ? { ...prev, has_seen_install_prompt: true } : prev);
  };

  return <InstallPrompt onDismiss={handleDismiss} />;
};

// Module-level flag — survives re-renders and bypasses stale closure issues
let onboardingDismissed = false;

// Persistent onboarding companion — visible until all 10 steps are ticked
const GlobalOnboardingCarousel = () => {
  const { user, setUser } = useAuth();
  const [, forceUpdate] = useState(0);

  if (onboardingDismissed) return null;
  if (!user || user.onboarding_complete === true) return null;

  const stepsComplete = user.onboarding_steps_complete || Array(10).fill(false);

  const handleToggle = (idx) => {
    // Derive next array inside setUser so we always read current state,
    // not the stale closure snapshot of stepsComplete.
    setUser(prev => {
      if (!prev) return prev;
      const current = prev.onboarding_steps_complete || Array(10).fill(false);
      const next = current.map((v, i) => i === idx ? true : v);
      const allDone = next.every(Boolean);
      // Fire-and-forget PATCH; carousel already PATCHes on every tick
      apiClient.patch("/user/profile", { onboarding_steps_complete: next }).catch(() => {});
      return { ...prev, onboarding_steps_complete: next, ...(allDone ? { onboarding_complete: true } : {}) };
    });
  };

  return (
    <OnboardingCarousel
      stepsComplete={stepsComplete}
      onToggle={handleToggle}
      onDismissAll={() => { onboardingDismissed = true; forceUpdate(n => n + 1); }}
    />
  );
};

// Service Worker Update Banner
const UpdateBanner = () => {
  const [showUpdate, setShowUpdate] = useState(false);

  useEffect(() => {
    if (!navigator.serviceWorker) return;
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'SW_UPDATED') {
        setShowUpdate(true);
      }
    });
    // Also check on load if there's a waiting SW
    navigator.serviceWorker.ready.then(reg => {
      if (reg.waiting) setShowUpdate(true);
      reg.addEventListener('updatefound', () => {
        const newSW = reg.installing;
        if (newSW) {
          newSW.addEventListener('statechange', () => {
            if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
              setShowUpdate(true);
            }
          });
        }
      });
    });
  }, []);

  if (!showUpdate) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[200] bg-[#F5A623] text-black text-center py-2 px-4 text-sm font-medium" data-testid="update-banner">
      Update available
      <button onClick={() => window.location.reload()} className="ml-3 underline font-bold">Tap to reload</button>
    </div>
  );
};

// Toaster that follows the active theme (sonner accepts "light" | "dark").
function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster position="bottom-right" theme={theme} />;
}

function App() {
  useEffect(() => {
    // Dismiss Safari's search bar overlay when returning from native app links (Waze, CityMapper, Maps)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Small delay to let Safari render the page first
        requestAnimationFrame(() => {
          // Scroll to reset Safari's UI state (clears the search bar)
          window.scrollTo(0, 0);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return (
    <div className="App min-h-screen bg-background">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <UpdateBanner />
            <OfflineBanner />
            <GlobalInstallPrompt />
            <AppRouter />
            <ThemedToaster />
            <GlobalOnboardingCarousel />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
